import { useState, useCallback, useEffect, useRef } from 'react';
import { usePersistedState } from './usePersistedState';
import type { ToolbarPosition, UseToolbarDragOptions, UseToolbarDragReturn } from '../types';
import { DEFAULT_TOOLBAR_POSITION, TOOLBAR_STORAGE_KEY } from '../types';

const TOOLBAR_WIDTH = 48;
const TOOLBAR_MIN_VISIBLE = 32;

function clampPosition(
  pos: ToolbarPosition,
  toolbarEl: HTMLDivElement | null,
  sidebarWidth: number,
  sidebarOpen: boolean,
): ToolbarPosition {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const effectiveSidebar = sidebarOpen ? sidebarWidth : 0;
  const toolbarHeight = toolbarEl?.offsetHeight ?? 200;

  const maxX = vw - effectiveSidebar - TOOLBAR_MIN_VISIBLE;
  const maxY = vh - TOOLBAR_MIN_VISIBLE;

  return {
    x: Math.max(0, Math.min(pos.x, maxX)),
    y: Math.max(0, Math.min(pos.y, maxY - toolbarHeight + TOOLBAR_MIN_VISIBLE)),
  };
}

/**
 * Hook for drag-positioning of the floating toolbar.
 * Uses pointer events + setPointerCapture for smooth dragging.
 * Persists position to localStorage via usePersistedState.
 */
export function useToolbarDrag(options: UseToolbarDragOptions): UseToolbarDragReturn {
  const { sidebarWidth, sidebarOpen, toolbarRef } = options;

  const [persisted, setPersisted] = usePersistedState<ToolbarPosition>(
    TOOLBAR_STORAGE_KEY,
    DEFAULT_TOOLBAR_POSITION,
  );

  const [position, setPosition] = useState<ToolbarPosition>(() =>
    clampPosition(persisted, null, sidebarWidth, sidebarOpen),
  );
  const [isDragging, setIsDragging] = useState(false);

  const dragOffset = useRef({ x: 0, y: 0 });
  const rafId = useRef<number | null>(null);
  const latestPos = useRef(position);

  // Sync persisted → position on mount
  useEffect(() => {
    latestPos.current = position;
  }, [position]);

  // Persist position changes (debounced through usePersistedState)
  useEffect(() => {
    setPersisted(position);
  }, [position, setPersisted]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // Only primary button
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);

    dragOffset.current = {
      x: e.clientX - latestPos.current.x,
      y: e.clientY - latestPos.current.y,
    };
    setIsDragging(true);

    const handlePointerMove = (ev: PointerEvent) => {
      const newPos = {
        x: ev.clientX - dragOffset.current.x,
        y: ev.clientY - dragOffset.current.y,
      };

      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
      }
      rafId.current = requestAnimationFrame(() => {
        rafId.current = null;
        const clamped = clampPosition(
          newPos,
          toolbarRef.current,
          sidebarWidth,
          sidebarOpen,
        );
        latestPos.current = clamped;
        setPosition(clamped);
      });
    };

    const handlePointerUp = () => {
      setIsDragging(false);
      target.removeEventListener('pointermove', handlePointerMove);
      target.removeEventListener('pointerup', handlePointerUp);
    };

    target.addEventListener('pointermove', handlePointerMove);
    target.addEventListener('pointerup', handlePointerUp);
  }, [sidebarWidth, sidebarOpen, toolbarRef]);

  // Recalculate clamping on resize or sidebar toggle
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampPosition(prev, toolbarRef.current, sidebarWidth, sidebarOpen));
    };

    window.addEventListener('resize', handleResize);
    // Also reclamp when sidebar changes
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [sidebarWidth, sidebarOpen, toolbarRef]);

  return {
    position,
    isDragging,
    dragHandleProps: {
      onPointerDown: handlePointerDown,
    },
  };
}
